import { ROUTES } from 'utils/constants/constants_routes';
import { sendDataForTracking } from 'utils/routeTracking';
import {
  sendSteloEventsNames,
  STELO_UNFINISHED_EVENT,
} from 'pages/Stelo/steloEvents';
import { useApplicationResolution } from 'hooks/utilities/useApplicationResolution';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useErrorManager } from 'InternalLibrary';
import { useLocation, useNavigate } from 'react-router-dom';
import axiosInstance from 'utils/axiosInstance';
import useApplication from 'hooks/useApplication';
import useClient from 'hooks/useClient';

type SteloMessageEventRaw = {
  origin: string;
  data: {
    eventName: string;
    type: string;
  };
};

export type SteloEventDatesType = {
  dateStartFlow: null | Date;
  dateStartScreen: null | Date;
  secondsLoadingTime: number;
  customErrorEventSent: boolean;
  flowFinished: boolean;
};

export const useStelo = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [iframeUrl, setIframeUrl] = useState<string | undefined>();
  const { setError } = useErrorManager();
  const { navigateByApplicationResolution } = useApplicationResolution();
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const { client } = useClient();
  const { data: application } = useApplication();

  // Mutable ref holds event timing state so updates don't trigger re-renders
  const SteloEventsDataRef = useRef<SteloEventDatesType>({
    dateStartFlow: null,
    dateStartScreen: null,
    secondsLoadingTime: 0,
    customErrorEventSent: false,
    flowFinished: false,
  });

  const navigate = useNavigate();
  const { pathname } = useLocation();

  // Determines which post-stelo route to navigate to after completion
  const isApprovedRoute = pathname === ROUTES.STELO_APPROVED;

  const onSubmitStelo = () => {
    if (!SteloEventsDataRef.current.flowFinished) {
      sendSteloEventsNames(STELO_UNFINISHED_EVENT, SteloEventsDataRef);
    }

    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    // Constant for approved route because there is only one option
    setIframeUrl(undefined);
    if (isApprovedRoute) {
      navigate(ROUTES.FIRST_LOAN_ONLINE_BANK_APPROVED);
    } else {
      navigateByApplicationResolution();
    }
  };

  const getSteloLink = useCallback(async () => {
    const {
      data: { executionToken },
    } = await axiosInstance.get('/client/stelo-auth-token');
    return `${
      import.meta.env.VITE_STELO_URL
    }/application?auth_token=${executionToken}`;
  }, []);

  const receiveSteloStatusData = useCallback(
    (event: SteloMessageEventRaw) => {
      // Ignore messages from origins other than the Stelo iframe
      if (!event.origin.includes(import.meta.env.VITE_STELO_URL)) {
        return;
      }

      const eventName = event.data?.eventName;

      sendSteloEventsNames(eventName, SteloEventsDataRef);
      switch (eventName) {
        case 'execution:workflow.step.apicallandanalysis.apicall:completed':
          // Set a 5-second timeout to automatically call onSubmitStelo
          timeoutRef.current = setTimeout(() => {
            onSubmitStelo();
          }, 5000);
          break;
        default:
          break;
      }
    },
    [onSubmitStelo],
  );

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.addEventListener('message', receiveSteloStatusData, false);
      return () => {
        window.removeEventListener('message', receiveSteloStatusData, false);
        // Clear any existing timeout on cleanup
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
      };
    }
  }, [receiveSteloStatusData]);

  const onSubmit = async () => {
    try {
      setLoading(true);
      const link = await getSteloLink();
      if (link) {
        setIframeUrl(link);
        sendDataForTracking({
          typeEvent: 'event',
          details: 'stelo_open',
          client,
          application,
        });
      }
    } catch (error) {
      setError(error);
    } finally {
      setLoading(false);
    }
  };

  return {
    iframeUrl,
    isApprovedRoute,
    loading,
    onSubmit,
    onSubmitStelo,
  };
};
